// WHAT: React Context for managing roadmap items and filter state globally
// WHY:  Provides centralized state management for roadmap data across the application
// DOMAIN: Admin → Roadmap
// DEPENDENCIES: React Context API, Roadmap_Item type
// RISKS:
//   - NOTE: Context state is not persisted - data is lost on page refresh
//   - NOTE: No validation of filter values
//   - NOTE: setItems can be called with invalid data

import {
  createContext,
  useContext,
  useState,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
} from "react";

import { type Roadmap_Item } from "../types/roadmap-types";

/**
 * Filter configuration for roadmap items
 */
interface RoadmapFilter {
  /** Filter by module name */
  module?: string;
  /** Search query for title/description */
  search?: string;
  /** Whether to show completed items */
  showCompleted?: boolean;
  /** Filter by item status */
  status?: string;
  /** Filter by item type */
  type?: string;
}

/**
 * Roadmap context state interface
 */
interface RoadmapContextState {
  /** Current filter configuration */
  filter: RoadmapFilter;
  /** Array of all roadmap items */
  items: Roadmap_Item[];
  /** Function to update filter */
  setFilter: (filter: RoadmapFilter) => void;
  /** Function to update roadmap items */
  setItems: Dispatch<SetStateAction<Roadmap_Item[]>>;
}

// NOTE: Create context with undefined default to enforce provider usage
const RoadmapContext = createContext<RoadmapContextState | undefined>(undefined);

/**
 * WHAT:
 *   Context provider component that manages roadmap items and filter state.
 *
 * WHY:
 *   Provides global state management for roadmap data, allowing components
 *   throughout the application to access and update roadmap items and filters.
 *
 * BEHAVIOR:
 *   - Initializes items as empty array
 *   - Initializes filter with showCompleted: false
 *   - Provides state and setters to child components via context
 *
 * RISKS:
 *   - NOTE: State is not persisted - data must be fetched on mount
 *   - NOTE: No validation of items or filter values
 *
 * @param root0
 *
 * @param root0.children
 *
 * @returns {JSX.Element} Die gerenderte RoadmapProvider-Komponente
 *
 */
export const RoadmapProvider = ({ children }: { children: ReactNode }) => {
  // NOTE: State for all roadmap items
  const [items, setItems] = useState<Roadmap_Item[]>([]);
  // NOTE: State for filter configuration (default: hide completed items)
  const [filter, setFilter] = useState<RoadmapFilter>({
    showCompleted: false,
  });

  return (
    <RoadmapContext.Provider value={{ filter, items, setFilter, setItems }}>
      {children}
    </RoadmapContext.Provider>
  );
};

/**
 * WHAT:
 *   Custom hook to access roadmap context state.
 *
 * WHY:
 *   Provides type-safe access to roadmap context with runtime validation.
 *
 * RETURNS:
 *   RoadmapContextState — Context state with items, setItems, filter, setFilter
 *
 * @returns {RoadmapContextState} Context-State mit items, setItems, filter, setFilter
 *
 * BEHAVIOR:
 *   - Retrieves context value
 *   - Throws error if used outside RoadmapProvider
 *
 * RISKS:
 *   - NOTE: Throws error if used outside provider - ensure provider wraps components
 *
 */
export const useRoadmapContext = (): RoadmapContextState => {
  const context = useContext(RoadmapContext);
  if (!context) {
    throw new Error("useRoadmapContext must be used within a RoadmapProvider");
  }
  return context;
};
